// @ts-nocheck
import React, { useState, useEffect, useContext, useRef } from 'react';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import SCInput from '@/components/sc-controls/form-controls/sc-input';
import SCPasswordInput from '@/components/sc-controls/form-controls/sc-password-input';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme/';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import ConfirmAction from '@/components/modals/confirm-action';
import ConfirmAccess from '@/components/modals/auth/confirm-access';
import CedeOwnership from '@/components/modals/employee/cede-ownership';
import ColourPicker from '@/components/colour-picker';
import PS from '@/services/permission/permission-service';
import Tabs from '@/components/tabs';
import EmployeeStore from '@/components/employee/employee-store';
import EmployeeJobStatus from '@/components/employee/employee-job-status';
import EmployeeInviteHeader, { showsInvite } from '@/components/employee/employee-invite-header';
import EmployeeInvitePanel from '@/components/employee/employee-invite-panel';
import EmployeeReport from '@/components/employee/employee-report';
import MinuteSelector from '@/components/selectors/minute-selector';
import BillingService from '@/services/billing-service';
import SubscriptionContext from '@/utils/subscription-context';
import NoSSR from '@/utils/no-ssr'
import SCSwitch from '@/components/sc-controls/form-controls/sc-switch';
import Constants from '@/utils/constants';
import SCComboBox from "@/components/sc-controls/form-controls/sc-combobox";
import EmployeePermissionsTab from '@/components/employee/employee-permissions-tab';
import storage from '@/utils/storage';
import ChangeEmployeePassword from '@/components/modals/employee/change-employee-password';
import { Flex, Switch, Tooltip, Text } from '@mantine/core';
import { IconDeviceMobile, IconMail, IconMessage } from '@tabler/icons-react';
import employeeService from '@/services/employee/employee-service';
import { Button as MantineButton } from '@mantine/core';
import useRefState from '@/hooks/useRefState';
import { showNotification } from '@mantine/notifications';
import { createFileRoute } from '@tanstack/react-router'

function EmployeePage() {

  const router = useRouter();
  const { id } = router.query;
 // TODO(tanstack-router): verify router.query → useParams/useSearch

  const subscriptionContext = useContext(SubscriptionContext);

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const toast = useContext(ToastContext);
  const [employee, setEmployee] = useState<any>(null);
  const [employeeName, setEmployeeName] = useState('');
  const [saving, setSaving] = useState(false);
  const [resetTokenOnPermissionChange, setResetTokenOnPermissionChange] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [primaryRoles, setPrimaryRoles] = useState<any[]>([]);
  const [recentlyCreated, setRecentlyCreated] = useState<any>(null);
  const [inviteShare, setInviteShare] = useState<any>(null);

  useEffect(() => {
    if (!employee) return;
    if (typeof window !== "undefined" && recentlyCreated === null) {
      setRecentlyCreated(window.location.search?.toString().toLowerCase().includes("created=true"));
      try {
        const raw = sessionStorage.getItem('sc:inviteShare');
        if (raw) {
          setInviteShare(JSON.parse(raw));
          sessionStorage.removeItem('sc:inviteShare');
        }
      } catch { /* ignore */ }
    }

    setEmployeeName(employee.FirstName + ' ' + employee.LastName);
  }, [employee]);

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const [multiStoreTenant, setMultiStoreTenant] = useState(false);
  const [showTabs, setShowTabs] = useState(false);

  const [multiWorkflow, setMultiWorkflow] = useState(false);
  const [advancedPermission, setAdvancedPermission] = useState(false);

  const [customerStatus, setCustomerStatus] = useState<any>(null);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [tabOptions, setTabOptions] = useState<any[]>([]);

  const getSubscriptionInfo = async () => {
    const [subscriptionInfo, message] = await BillingService.getSubcriptionInfo(subscriptionContext);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
      setMultiStoreTenant(subscriptionInfo.MultiStore);
      setMultiWorkflow(subscriptionInfo.MultiWorkflow);
      setAdvancedPermission(subscriptionInfo.AdvancedPermission);
      setShowTabs(true);// subscriptionInfo.MultiStore || subscriptionInfo.MultiWorkflow || subscriptionInfo.AdvancedPermission);

      updateTabOptions(subscriptionInfo);

      setCustomerStatus(subscriptionInfo.CustomerStatus);

      if (subscriptionInfo.LicenseType == Enums.getEnumStringValue(Enums.LicenseType, Enums.LicenseType.Enterprise)) {
        setIsEnterprise(true);
      }
    }
  };

  const employeeHasActiveUser = () => {
    return inputs.canLogin && inputs.UserID;
  }

  const updateTabOptions = (subscriptionInfo: any) => {

    subscriptionInfo = subscriptionInfo ?? storage.getCookie(Enums.Cookie.subscriptionInfo);
    let tabs = [{ text: 'Details' }];
    if (employeeHasActiveUser()) {
      tabs.push({ text: 'Permissions', suppressCount: true });
    }
    if (subscriptionInfo.MultiStore) {
      tabs.push({ text: 'Stores', suppressCount: true });
    }
    if (employeeHasActiveUser() && (subscriptionInfo.MultiWorkflow || subscriptionInfo.AdvancedPermission)) {
      tabs.push({ text: 'Job Statuses', suppressCount: true });
    }
    if (employeeHasActiveUser() && (subscriptionInfo.AdvancedPermission)) {
      tabs.push({ text: 'Reports', suppressCount: true });
    }
    setTabOptions(tabs);
  }

  const getPrimaryRoles = async () => {
    setPrimaryRoles(await employeeService.getPrimaryRoles());
  };

  useEffect(() => {
    getSubscriptionInfo();
    triggerMinuteDebounce();
    getPrimaryRoles();
  }, []);

  const triggerMinuteDebounce = () => {
    setTimeout(() => {
      minuteDebounce.current = true;
    }, 500);
  };

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [confirmEmployeeAccessOptions, setConfirmEmployeeAccessOptions] = useState({
    display: false,
    onConfirm: () => { },
  });

  const [showCedeOwnership, setShowCedeOwnership] = useState(false);

  const [inputs, setInputs, getInputsValue] = useRefState({
    FirstName: '',
    LastName: '',
    EmailAddress: '',
    MobileNumber: '',
    IsTechnician: false,
    IsSalesRepresentative: false,
    canLogin: false,
    UserName: '',
    DisplayColor: '',
    SendSMS: false,
    SendEmail: false,
    SendNotifications: false,
    IsActive: false,
    UserID: null,
    password: '',
    UserIsActive: false,
    ownerPassword: '',
    confirmOwnerPassword: '',
    SessionTimeOut: 0,
    IsAllStore: false,
    IsAllJobCardStatus: false,
    IsAllReport: false,
    PrimaryRole: '',
    primaryRoleText: '',
    otherPrimaryRoleText: ''
  });

  useEffect(() => {
    updateTabOptions();
  }, [inputs.canLogin, inputs.UserID]);

  const [inputErrors, setInputErrors] = useState<any>({});

  const minuteDebounce = useRef(false);
  const saveEmployeeRef = useRef<any>(null);

  const { data: employeeData } = useQuery(
    ['employee', id],
    () => Fetch.get({ url: `/Employee?id=${id}` }),
    { enabled: !!id }
  );

  useEffect(() => {
    setEmployee(null);
  }, [id]);

  useEffect(() => {
    if (employeeData && !employee) {
      setEmployee(employeeData);
      setInputs({
        FirstName: employeeData.FirstName,
        LastName: employeeData.LastName,
        EmailAddress: employeeData.EmailAddress,
        MobileNumber: employeeData.MobileNumber,
        IsTechnician: employeeData.IsTechnician,
        IsSalesRepresentative: employeeData.IsSalesRepresentative,
        canLogin: employeeData.AuthUserIsActive,
        UserName: employeeData.UserName,
        DisplayColor: employeeData.DisplayColor,
        SendSMS: employeeData.SendSMS,
        SendEmail: employeeData.SendEmail,
        SendNotifications: employeeData.SendNotifications,
        IsActive: employeeData.IsActive,
        UserID: employeeData.UserID,
        password: '',
        UserIsActive: employeeData.AuthUserIsActive,
        ownerPassword: '',
        confirmOwnerPassword: '',
        SessionTimeOut: employeeData.SessionTimeOut,
        IsAllStore: employeeData.IsAllStore,
        IsAllJobCardStatus: employeeData.IsAllJobCardStatus,
        IsAllReport: employeeData.IsAllReport,
        PrimaryRole: employeeData.PrimaryRole || '',
        primaryRoleText: employeeData.PrimaryRole?.startsWith('Other: ') ? 'Other' : (employeeData.PrimaryRole || ''),
        otherPrimaryRoleText: employeeData.PrimaryRole?.startsWith('Other: ') ? employeeData.PrimaryRole.replace('Other: ', '') : ''
      });
    }
  }, [employeeData]);

  useEffect(() => {
    if (!employee) return;
    if (!inputs.canLogin && !employee.UserID) {
      setInputs({ ...inputs, UserName: null });
    } else if (inputs.canLogin) {
      setInputs({ ...inputs, UserName: inputs.UserName ? inputs.UserName : inputs.EmailAddress, UserIsActive: true });
    } else if (!inputs.canLogin) {
      setInputs({ ...inputs, UserIsActive: false });
    }
  }, [inputs.canLogin]);

  const [selectedTab, setSelectedTab] = useState('Details');

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, (...args: any[]) => saveEmployeeRef.current?.(...args));

  if (!employee) return null;

  const initialCanLogin = employee.AuthUserIsActive;
  const canChangePassword = employeeHasActiveUser() && ((employee.OwnerControlled || !initialCanLogin) && PS.hasPermission(Enums.PermissionName.Owner, true)
    || (employee.OwnerControlled && PS.hasPermission(Enums.PermissionName.UserManagement, true)));

  const handleInputChange = (e: any) => {

    let setter = {
      ...inputs,
      [e.name]: e.value
    };

    if (e.name === 'primaryRoleText') {
      if (e.value === 'Other') {
        setter.PrimaryRole = 'Other: ' + inputs.otherPrimaryRoleText
      } else {
        setter.PrimaryRole = e.value
      }
    }

    if (e.name === 'otherPrimaryRoleText') {
      setter.PrimaryRole = 'Other: ' + e.value
    }

    if (e.name === "EmailAddress" && !employee.UserID && inputs.canLogin) {
      setter.UserName = e.value;
    }

    if (e.name === "canLogin" && !employee.UserID && e.value === true) {
      setter.UserName = inputs.EmailAddress;
    }

    if (e.name === "canLogin" && !inputs.IsActive && e.value === true) {
      setter.IsActive = true;
    }

    setInputs(setter);

    if (e.name !== "ownerPassword" && e.name !== "confirmOwnerPassword") {
      setFormIsDirty(true);
    }
  };

  const toggleActivation = async () => {
    if (PS.isMasterSystemAdmin()) return;

    const doToggle = async () => {

      let reactiveInps = {
        IsActive: !inputs.IsActive
      };

      if (!inputs.canLogin && reactiveInps.IsActive && employee.UserID) {
        (reactiveInps as any).canLogin = true;
      }

      setInputs((inps: any) => {
        return {
          ...inps,
          ...reactiveInps
        }
      });
      setFormIsDirty(true);
      await saveEmployee();
    };

    let deactivate = inputs.IsActive;

    if (deactivate) {
      setConfirmOptions({
        ...Helper.initialiseConfirmOptions(),
        display: true,
        heading: deactivate ? "Deactivate Employee" : "Reactivate Employee",
        text: deactivate && employeeHasActiveUser() ? "By confirming deactivating the employee, login access will be removed." : "Confirm deactivate employee?",
        confirmButtonText: "Confirm",
        onConfirm: async () => {
          await doToggle();
        }
      });
    }
    else {
      await doToggle();
    }
  };

  const promptForTemplatePermissions = async () => {
    return new Promise((resolve: any, reject: any) => {
      setConfirmOptions({
        ...Helper.initialiseConfirmOptions(),
        display: true,
        cancelButtonText: "Leave as is",
        confirmButtonText: "Reset Permissions",
        heading: "Primary Role Changed",
        text: `You have changed the user's primary role. Would you like to reset their permissions to default?`,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  }

  const saveEmployee = async (propertyName: any, value: any) => {
    setSaving(true);

    const inps = { ...getInputsValue() };

    let validationItems = [
      { key: 'FirstName', value: inps.FirstName, required: true, type: Enums.ControlType.Text },
      { key: 'LastName', value: inps.LastName, required: true, type: Enums.ControlType.Text },
      { key: 'EmailAddress', value: inps.EmailAddress, required: true, type: Enums.ControlType.Email, rejectServCraft: true },
      { key: 'MobileNumber', value: inps.MobileNumber, required: false, type: Enums.ControlType.ContactNumber },
      { key: 'PrimaryRole', value: inps.PrimaryRole, required: true, type: Enums.ControlType.Text },
    ];

    if (inps.canLogin === true) {

      if (inps.password || inps.confirmPassword || !inps.UserID) {
        validationItems.push({
          key: 'password', value: inps.password, required: true, type: Enums.ControlType.Text
        });

        //   key: 'confirmPassword', value: inputs.confirmPassword, required: true, type: Enums.ControlType.Text, equalsPassword: inputs.password
      }

      validationItems.push({
        key: 'SessionTimeOut', value: inps.SessionTimeOut, required: true, type: Enums.ControlType.Number, lte: 10080, gte: 0
      });

    }

    let { isValid, errors } = Helper.validateInputs(validationItems);

    // Global password strength validation (min 10 chars, composition, common words)
    if (inps.canLogin === true && (inps.password || !inps.UserID)) {
      const userwords = [inps.FirstName, inps.LastName, inps.EmailAddress, inps.MobileNumber, inps.UserName];
      const strengthError = Helper.getPasswordStrengthError(inps.password, { userwords });
      if (strengthError) {
        isValid = false;
        (errors as any).password = strengthError;
      }
      if (inps.password && inps.password.length > 100) {
        isValid = false;
        (errors as any).password = 'Please use a password of 100 characters or less';
      }
    }

    if (errors["SessionTimeOut"]) {
      errors["SessionTimeOut"] = "Must be less than or equal to 7 days"
    }

    if (inps.primaryRoleText === 'Other' && !inps.otherPrimaryRoleText.trim()) {
      errors["otherPrimaryRoleText"] = `Please specify the employee role`;
      isValid = false;
    }

    if (inps.canLogin === true && inps.SessionTimeOut < Constants.minimumSessionTimeoutMinutes && inps.SessionTimeOut > 0) {
      errors["SessionTimeOut"] = `Must be greater than ${Constants.minimumSessionTimeoutMinutes} minutes or leave as 0 for infinite`;
      isValid = false;
    }

    setInputErrors(errors);

    if (isValid) {

      let retemplatePermissions = false;
      if (employee.PrimaryRole !== inps.PrimaryRole && inps.IsActive && inps.canLogin) {
        retemplatePermissions = await promptForTemplatePermissions(employee.PrimaryRole, inps.PrimaryRole);
      }

      let employeeToSave = {
        ...employee,
        FirstName: inps.FirstName,
        LastName: inps.LastName,
        EmailAddress: inps.EmailAddress,
        MobileNumber: inps.MobileNumber,
        IsTechnician: inps.IsTechnician,
        IsSalesRepresentative: inps.IsSalesRepresentative,
        DisplayColor: inps.DisplayColor,
        SendSMS: inps.SendSMS,
        SendEmail: inps.SendEmail,
        SendNotifications: inps.SendNotifications,
        IsActive: inps.IsActive,
        PrimaryRole: inps.PrimaryRole,
      };

      if (propertyName) {
        employeeToSave[propertyName] = value;
      }

      const employeeBefore = await Fetch.get({
        url: `/Employee?id=${employeeToSave.ID}`
      });

      let userToSave = null;
      if (inps.canLogin || inps.UserName) {
        userToSave = {
          UserName: inps.UserName,
          ID: inps.UserID,
          Password: inps.password,
          IsActive: employeeToSave.IsActive && inps.canLogin,
          SessionTimeOut: inps.SessionTimeOut
        };
      }

      const employeePut = await Fetch.put({
        url: '/Employee',
        params: {
          Employee: employeeToSave,
          PermissionList: [],
          User: userToSave,
          StoreList: employeeBefore.Stores ? employeeBefore.Stores.map((x: any) => x.ID) : [],
          JobStatusList: employeeBefore.JobStatuses ? employeeBefore.JobStatuses.map((x: any) => x.ID) : [],
          ReportList: employeeBefore.Reports ? employeeBefore.Reports.map((x: any) => x.ID) : [],
          ResetTokenOnPermissionChange: resetTokenOnPermissionChange,
          RetemplatePermissions: retemplatePermissions
        }
      });
      if (employeePut.ID) {
        setFormIsDirty(false);
        setEmployee(employeePut);
        setInputs({
          ...inps,
          FirstName: employeePut.FirstName,
          LastName: employeePut.LastName,
          EmailAddress: employeePut.EmailAddress,
          MobileNumber: employeePut.MobileNumber,
          IsTechnician: employeePut.IsTechnician,
          IsSalesRepresentative: employeePut.IsSalesRepresentative,
          canLogin: employeePut.AuthUserIsActive,
          UserName: employeePut.UserName,
          DisplayColor: employeePut.DisplayColor,
          SendSMS: employeePut.SendSMS,
          SendEmail: employeePut.SendEmail,
          SendNotifications: employeePut.SendNotifications,
          IsActive: employeePut.IsActive,
          UserID: employeePut.UserID,
          UserIsActive: employeePut.AuthUserIsActive,
          SessionTimeOut: employeePut.SessionTimeOut,
          IsAllStore: employeePut.IsAllStore,
          IsAllJobCardStatus: employeePut.IsAllJobCardStatus,
          IsAllReport: employeePut.IsAllReport,
          PrimaryRole: employeePut.PrimaryRole || "",
          primaryRoleText: employeePut.PrimaryRole?.startsWith('Other: ') ? 'Other' : (employeePut.PrimaryRole || ""),
          otherPrimaryRoleText: employeePut.PrimaryRole?.startsWith('Other: ') ? employeePut.PrimaryRole.replace('Other: ', '') : ''
        });
        toast.setToast!!!({
          message: 'Employee saved successfully',
          show: true,
          type: 'success'
        });
        setRecentlyCreated(false);
      } else if (employeePut?.Message) {
        const msg = employeePut.Message;
        if (inps.password) {
          setInputErrors({ ...inputErrors, password: msg });
        }
      } else if (employeePut?.message || employeePut?.serverMessage) {
        const msg = employeePut.message || employeePut.serverMessage;
        showNotification({ color: 'yellow.7', message: msg, autoClose: 4000 });
      } else {
        showNotification({ color: 'yellow.7', message: 'Something went wrong', autoClose: 4000 });
      }
    } else {
      toast.setToast!!!({
        message: 'There are errors on the page.',
        show: true,
        type: 'error'
      });
    }

    setSaving(false);

    return isValid;
  }

  saveEmployeeRef.current = saveEmployee;

  const setColour = (colour: any) => {
    setInputs({ ...inputs, DisplayColor: colour });
    setFormIsDirty(true);
  };

  const understandMoreClicked = () => {
    setConfirmEmployeeAccessOptions({
      display: true,
      onConfirm: () => {
      },
    });
  };

  const handleCanLoginChange = (e: any) => {
    if (PS.isMasterSystemAdmin()) return;

    handleInputChange(e);

    // if (inputs.canLogin) {
    //   if (inputs.UserIsActive) {
    //       ...Helper.initialiseConfirmOptions(),
    //       confirmButtonText: "Remove access",
    //       onConfirm: () => {
    //       },
    //       display: true,
    //       heading: "Remove login access?",
    //       text: "Confirm removing login access for the employee?"
    //   } else {
    // } else {
    //   if (!inputs.UserIsActive && !isTrial) {
    //       display: true,
    //       onConfirm: () => {
    //       },
    //   } else {

  };

  const forceLogout = async () => {
    setConfirmOptions({
      ...Helper.initialiseConfirmOptions(),
      display: true,
      confirmButtonText: "Logout employee",
      heading: "Logout Employee?",
      text: "Note that it may cause them to lose all unsaved progress",
      onConfirm: async () => {
        const result = await Fetch.put({
          url: `/Employee/ForceLogout/${employee.ID}`
        });

        if (result === true) {
          toast.setToast!!!({
            message: "Employee successfully logged out",
            show: true,
            type: Enums.ToastType.success
          });
        }
      }
    });
  };

  const refreshInvite = async () => {
    const fresh = await Fetch.get({ url: `/Employee?id=${employee.ID}` });
    if (!fresh) return;
    setEmployee((current: any) => ({
      ...current,
      InviteStatus: fresh.InviteStatus,
      InviteIssuedAt: fresh.InviteIssuedAt,
      InviteExpiresAt: fresh.InviteExpiresAt,
      AuthUserIsActive: fresh.AuthUserIsActive,
    }));
    setInputs((current: any) => ({
      ...current,
      canLogin: fresh.AuthUserIsActive,
      UserIsActive: fresh.AuthUserIsActive,
    }));
  };

  const inviteState = showsInvite(employee?.InviteStatus);

  const channelChip = (name: string, icon: any, label: string) => (
    <MantineButton
      variant={inputs[name] ? 'light' : 'default'}
      radius="sm"
      leftSection={icon}
      aria-pressed={!!inputs[name]}
      onClick={() => handleInputChange({ name, value: !inputs[name] })}
    >
      {label}
    </MantineButton>
  );

  const employeeDetails = () => {
    return (<NoSSR>
      <div className="container">
        <div className="row">
          <div className="column">
            {recentlyCreated && !initialCanLogin && (
              <div>
                <div style={{ marginTop: "1rem", marginBottom: "1rem", fontSize: "1.3rem", fontWeight: "bold" }}>Congratulations on adding your employee!</div>
                <div>{employee.FirstName} will only be able to login if you enable login access.</div>
              </div>
            )}

            <EmployeeInviteHeader
              name={`${inputs.FirstName || ''} ${inputs.LastName || ''}`.trim() || inputs.EmailAddress || 'Employee'}
              status={employee.InviteStatus}
              isActive={inputs.IsActive}
              canSignIn={!!employeeHasActiveUser()}
              colour={inputs.DisplayColor}
            />
            {employee.Owner && <>
              <div>
                <div>{employee.FirstName} is the owner of the subscription. In order to deactivate them,
                  they will first need to&nbsp;
                  {PS.hasPermission(Enums.PermissionName.Owner, true) ?
                    <span onClick={() => setShowCedeOwnership(true)} className="permission-link">cede ownership</span>
                    :
                    "cede ownership "}
                  &nbsp;to another employee with login access.</div>
              </div>
            </>}
          </div>
          <div className="column column-end">
            <div className="actions">
              <MantineButton disabled={saving} onClick={saving ? null : saveEmployee} ml={"0.5rem"} >
                {saving ? "Saving" : 'Save'}
              </MantineButton>
              {(!inviteState || !inputs.IsActive) && (
                <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot toggle employee activation" : (employee.Owner ? "Owner cannot be deactivated without ceding ownership first" : "")} disabled={!PS.isMasterSystemAdmin() && !employee.Owner}>
                  <MantineButton variant="light" color={inputs.IsActive ? 'red' : "green"} disabled={employee.Owner || saving || PS.isMasterSystemAdmin()} onClick={() => !employee.Owner && toggleActivation()} ml={"0.5rem"}>
                    {inputs.IsActive ? 'Deactivate' : "Activate"}
                  </MantineButton>
                </Tooltip>
              )}
              <Flex style={{ marginRight: "1rem" }}>
                {canChangePassword && !inviteState ?
                  <>
                    <MantineButton variant="subtle" onClick={() => setChangePasswordOpen(true)} ml={"0.5rem"} disabled={saving}>
                      Update Password
                    </MantineButton>
                    <ChangeEmployeePassword
                      targetEmail={employee.UserName}
                      isOpen={changePasswordOpen}
                      setIsOpen={setChangePasswordOpen}
                      userwords={[employee.FirstName, employee.LastName, employee.EmailAddress, employee.MobileNumber, employee.UserName]}
                    />
                  </>
                  : ''}

                {/* 
                {employee.Owner && PS.hasPermission(Enums.PermissionName.Owner, true) ?
                  <MantineButton variant='subtle' onClick={() => setShowCedeOwnership(true)} ml={"0.5rem"} >
                    Cede Ownership
                  </MantineButton>
                  : ''
                } */}
              </Flex>

            </div>
          </div>
        </div>

        {recentlyCreated && initialCanLogin && (
          <Text size="sm" mb="md" c="dimmed">
            We have pre-set permissions based on the role you selected. You can <span onClick={() => doSetSelectedTab("Permissions")} className="permission-link">review permissions</span> now or later.
          </Text>
        )}

        <div className="form-col">
          <EmployeeInvitePanel
            employeeId={employee.ID}
            status={employee.InviteStatus}
            issuedAt={employee.InviteIssuedAt}
            expiresAt={employee.InviteExpiresAt}
            heldLink={inviteShare}
            onChanged={refreshInvite}
            setConfirmOptions={setConfirmOptions}
          />

          <div className="two">
            <SCInput
              onChange={handleInputChange}
              error={inputErrors.FirstName}
              label={"First name"}
              name="FirstName"
              required={true}
              value={inputs.FirstName}
            />
            <SCInput
              onChange={handleInputChange}
              error={inputErrors.LastName}
              label={"Last name"}
              name="LastName"
              required={true}
              value={inputs.LastName}
            />
          </div>
          <div className="two">
            <SCInput
              onChange={handleInputChange}
              error={inputErrors.EmailAddress}
              label={"Email"}
              name="EmailAddress"
              required={true}
              value={inputs.EmailAddress}
              placeholder="email@address.com"
            />
            <SCInput
              onChange={handleInputChange}
              error={inputErrors.MobileNumber}
              label={"Mobile"}
              name="MobileNumber"
              type="tel"
              value={inputs.MobileNumber}
            />
          </div>

          <SCComboBox
            canSearch={false}
            onChange={(value: any) => handleInputChange({ name: 'primaryRoleText', value })}
            error={inputErrors.PrimaryRole}
            label={"Role"}
            name="primaryRoleText"
            required
            options={primaryRoles}
            value={inputs.primaryRoleText}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Sets what they can see and do.
            {employeeHasActiveUser() && <>
              {' '}<span className="permission-link" onClick={() => !saving && doSetSelectedTab("Permissions")}>Review permissions</span>
            </>}
          </Text>
          {inputs.primaryRoleText === 'Other' &&
            <SCInput
              onChange={handleInputChange}
              error={inputErrors.otherPrimaryRoleText}
              label={"Other Primary Role"}
              name="otherPrimaryRoleText"
              required
              value={inputs.otherPrimaryRoleText}
            />
          }

          {!inviteState && ((accessStatus !== Enums.AccessStatus.LockedWithOutAccess && accessStatus !== Enums.AccessStatus.LockedWithAccess)
            || customerStatus === "Trial" || inputs.canLogin) &&
            <Flex align={"center"} mt={"lg"}>
              <Tooltip label={PS.isMasterSystemAdmin() ? "ServCraft Admin cannot change login access" : ""} disabled={!PS.isMasterSystemAdmin()}>
                <Switch
                  style={{ display: "inline-block" }}
                  onLabel={"Login Access"}
                  offLabel={"Comms Only"}
                  size='xl'
                  checked={inputs.canLogin}
                  disabled={employee.Owner || PS.isMasterSystemAdmin()}
                  onChange={() => handleCanLoginChange({ name: 'canLogin', value: !inputs.canLogin })}
                />
              </Tooltip>
              <MantineButton variant="subtle" onClick={understandMoreClicked}>Understand More</MantineButton>
            </Flex>
          }

          {!inviteState && (inputs.canLogin ? <>
            <SCInput
              label={"Username"}
              value={inputs.UserName}
              readOnly={true}
            />
            {!inputs.UserID && <>
              <SCPasswordInput
                onChange={handleInputChange}
                error={inputErrors.password}
                label={"Password"}
                name="password"
                required={true}
                value={inputs.password}
                userwords={[inputs.FirstName, inputs.LastName, inputs.EmailAddress, inputs.MobileNumber, inputs.UserName]}
              />
              <Text size="sm" c="dimmed" mt="xs">
                Choose a strong password with at least 10 characters, including uppercase, lowercase, and a number. Skip common phrases like 'password', 'qwerty', '1234', or your name.
              </Text>
            </>}
            <MinuteSelector
              label="Session Timeout"
              hint="0 will never expire"
              value={inputs.SessionTimeOut}
              name="SessionTimeOut"
              required={true}
              error={inputErrors.SessionTimeOut}
              singleColumnView={true}
              onChange={(e: any) => {
                minuteDebounce.current && handleInputChange({ name: "SessionTimeOut", value: parseFloat(e) });
              }}
            />
          </>
            : inputs.UserName ?
              <SCInput
                label={"Existing Username"}
                value={inputs.UserName}
                readOnly={true}
              />
              : null
          )}

          <div className="group-label">Select communication channels</div>
          <Text size="xs" c="dimmed">For new jobs and appointments, job status changes, reschedules and cancellations.</Text>
          <Flex gap="sm" wrap="wrap" mt="sm">
            {channelChip('SendEmail', <IconMail size={15} />, 'Receive emails')}
            {channelChip('SendSMS', <IconMessage size={15} />, 'Receive SMS')}
            {employeeHasActiveUser() && channelChip('SendNotifications', <IconDeviceMobile size={15} />, 'Receive mobile push notifications')}
          </Flex>

          <div className="group-label">Set a colour for this employee</div>
          <ColourPicker selectedColour={inputs.DisplayColor} setColour={setColour} />
        </div>

        <style jsx>{`

        .padding-top {
          padding-top: 24px;
        }
        .form-col {
          max-width: 560px;
        }
        .two {
          display: flex;
          gap: 16px;
        }
        .two > :global(*) {
          flex: 1 1 0;
          min-width: 0;
        }
        .group-label {
          font-weight: 500;
          font-size: 14px;
          margin-top: 1.5rem;
        }

        .add-location {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
        }
        .add-location img {
          margin: 2px 0.5rem 0 0;
        }
        .column {
          display: flex;
          flex-direction: column;
          width: ${layout.inputWidth};
        }
        .column-end {
          align-items: flex-end;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 10rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .row-align {
          align-items: center;
          height: 1.75rem
        }
        .section {
          border-bottom: 1px solid #E8EDF2;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
        }
        .section:last-child {
          border-bottom: none;
        }
        .section h3 {
          color: ${colors.darkPrimary};
          font-size: 16px;
          margin: 0;
        }
        .section :global(.switch) {
          margin-top: 1rem;
        }
        .split {
          display: flex;
        }
        .actions {
          margin-top: 1rem;
            display: flex;
            flex-direction: row-reverse;
        }
        .actions :global(.button){
            margin-left: 0.5rem;
            margin-top: 1rem;
            padding: 0 1rem;
            white-space: nowrap;
        }
        .cede-ownership {
          display: flex;
          flex-direction: column;
          width: 100%;
          margin-top: 1rem;
        }

        .permission-link {
          color: ${colors.bluePrimary};
          font-weight: bold;
          cursor: pointer;
        }

        .permission-link:hover {
          text-decoration: underline;
        }

      `}</style>
      </div>
    </NoSSR>);
  }

  const doSetSelectedTab = (tabName: any) => {
    if (formIsDirty && selectedTab === "Details") {

      setConfirmOptions({
        ...Helper.initialiseConfirmOptions(),
        display: true,
        heading: "Save Changes?",
        text: "Would you like to save your changes before changing the tab?",
        confirmButtonText: "Save and Change",
        discardButtonText: "Don't Save",
        showCancel: true,
        showDiscard: true,
        onDiscard: async () => {
          setSelectedTab(tabName);
        },
        onConfirm: async () => {
          let saveSuccess = await saveEmployee();
          if (saveSuccess) {
            setSelectedTab(tabName);
          }
        }
      });

      return;
    }

    minuteDebounce.current = false;
    triggerMinuteDebounce();

    setSelectedTab(tabName);
  }

  const updateEmployeeProperty = async (propertyName: any, value: any) => {
    await saveEmployee(propertyName, value);
  }

  return (
    <div className="settings-container">
      {/*<div className="row">
        <div className="title">
          <Breadcrumbs
            crumbs={[
              { text: 'Settings', link: '', suppressLink: true },
              { text: 'Employees', link: '/settings/employee/list' },
              { text: `${employeeName}`, link: '' },
            ]}
          />
        </div>
      </div>*/}
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <div className="settings-card">
          {showTabs ?
            <>
              <Tabs
                disabled={saving}
                selectedTab={selectedTab}
                setSelectedTab={doSetSelectedTab}
                tabs={tabOptions}
              />
              {(() => {
                switch (selectedTab) {
                  case 'Details':
                    return employeeDetails();
                  case "Permissions":
                    return <EmployeePermissionsTab employee={employee} />
                  case 'Stores':
                    return <EmployeeStore employee={employee} accessStatus={accessStatus} customerStatus={customerStatus} updateEmployeeProperty={updateEmployeeProperty} employeeSaving={saving} />;
                  case 'Job Statuses':
                    return <EmployeeJobStatus employee={employee} accessStatus={accessStatus} customerStatus={customerStatus} isEnterprise={isEnterprise} updateEmployeeProperty={updateEmployeeProperty} employeeSaving={saving} />;
                  case 'Reports':
                    return <EmployeeReport employee={employee} accessStatus={accessStatus} customerStatus={customerStatus} updateEmployeeProperty={updateEmployeeProperty} employeeSaving={saving} />;
                }
              })()}
            </> :
            employeeDetails()
          }
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
      <ConfirmAccess options={confirmEmployeeAccessOptions} setOptions={setConfirmEmployeeAccessOptions} module={Enums.Module.Employee} moduleIsDeactivated={!inputs.IsActive} />

      {showCedeOwnership ?
        <CedeOwnership employeeID={employee.ID} setShowModal={setShowCedeOwnership} />
        : ''
      }

      <style jsx>{`
        .add-location {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
        }
        .add-location img {
          margin: 2px 0.5rem 0 0;
        }
        .column {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .column-end {
          align-items: flex-end;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 10rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .row-align {
          align-items: center;
          height: 1.75rem
        }
        .section {
          border-bottom: 1px solid #E8EDF2;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
        }
        .section:last-child {
          border-bottom: none;
        }
        .section h3 {
          color: ${colors.darkPrimary};
          font-size: 16px;
          margin: 0;
        }
        .section :global(.switch) {
          margin-top: 1rem;
        }
        .split {
          display: flex;
        }
        .actions {
          display: flex;
          align-items: end;
        }
        .actions :global(.button){
          margin-left: 0.5rem;
          margin-top: 0;
          padding: 0 1rem;
          white-space: nowrap;
        }
        .cede-ownership {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
      `}</style>
    </div>
  );
}

export default EmployeePage;

export const Route = createFileRoute('/_authenticated/settings/employee/$id')({ component: EmployeePage })
